import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineDate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Person = {
  id: number
  first_name: string
  last_name: string
  preferred_name: string
  full_name: string
  date_of_birth: string | null
  national_id: string
  passport_number: string
  tax_id: string
  personal_email: string
  mobile: string
  address_line1: string
  city: string
  postal_code: string
  country_code: string
  emergency_contact_name: string
  emergency_contact_phone: string
  photo_url: string
  user: number | null
  notes: string
  employment_count: number
  current_employment_id: number | null
  created_at: string
  updated_at: string
}

type Employment = {
  id: number
  person: number
  person_name: string
  subsidiary: number | null
  subsidiary_name: string | null
  subsidiary_code: string | null
  country_code: string
  governing_country: string
  employee_number: string
  start_date: string | null
  end_date: string | null
  end_reason: string
  status: 'PRE_HIRE' | 'ACTIVE' | 'ON_LEAVE' | 'ENDED'
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'INTERN'
  job_title: string
  department: number | null
  department_name: string | null
  manager: number | null
  manager_name: string | null
  is_currently_active: boolean
}

type TimeOff = {
  id: number
  employee: number | null
  employee_name: string | null
  employment: number | null
  person_name: string | null
  employment_number: string | null
  leave_type: 'VACATION' | 'SICK' | 'UNPAID' | 'PARENTAL' | 'OTHER'
  start_date: string
  end_date: string
  days: string
  reason: string
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
}

type SubsidiaryLite = { id: number; code: string; name: string; country_code: string }
type OrgInfo = {
  organization_type: 'SINGLE_COMPANY' | 'HOLDING_SUBSIDIARIES'
  group_country: string
  group_name: string
  legal_name: string
}

type NewEmploymentInput = {
  start_date: string
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'INTERN'
  subsidiary: number | null
  position: number | null
}

type PositionLite = {
  id: number
  code: string
  title: string
  is_active: boolean
  is_open: boolean
}

type ContractorEngagement = {
  id: number
  person: number
  person_name: string
  engagement_number: string
  contractor_type: 'DIRECT' | 'AGENCY'
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
  start_date: string
  end_date: string | null
  job_title: string
  employer_partner_name: string | null
  active_contract_number: string | null
  is_currently_active: boolean
}

type ProjectAssignment = {
  id: number
  project: number
  project_code: string
  project_name: string
  employee: number | null
  employee_name: string | null
  employment: number | null
  person_name: string | null
  employment_number: string | null
  role: 'CONSULTANT' | 'CONTRACTOR' | 'MANAGER'
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
}

const EMPLOYMENT_STATUS_COLOR: Record<Employment['status'], string> = {
  PRE_HIRE: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  ON_LEAVE: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-slate-100 text-slate-700',
}

const EMPLOYMENT_TYPE_LABEL: Record<Employment['employment_type'], string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  INTERN: 'Intern',
}

const LEAVE_LABEL: Record<TimeOff['leave_type'], string> = {
  VACATION: 'Vacation',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  PARENTAL: 'Parental leave',
  OTHER: 'Other',
}

const LEAVE_STATUS_COLOR: Record<TimeOff['status'], string> = {
  REQUESTED: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
}

const ASSIGNMENT_STATUS_COLOR: Record<ProjectAssignment['status'], string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-slate-100 text-slate-700',
}

type TabKey = 'profile' | 'employments' | 'timeoff' | 'projects' | 'files'

// ─────────────────── Page ───────────────────
export default function HRPersonDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const personId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const personQ = useQuery({
    queryKey: ['hr', 'person', personId],
    queryFn: async () => (await api.get<Person>(`/hr/persons/${personId}/`)).data,
    enabled: !!personId,
  })

  const employmentsQ = useQuery({
    queryKey: ['hr', 'employments', { person: personId }],
    queryFn: async () =>
      (await api.get<Employment[]>(`/hr/employments/?person=${personId}`)).data,
    enabled: !!personId,
  })

  const contractorEngagementsQ = useQuery({
    queryKey: ['hr', 'contractor-engagements', { person: personId }],
    queryFn: async () =>
      (await api.get<ContractorEngagement[]>(`/hr/contractor-engagements/?person=${personId}`)).data,
    enabled: !!personId,
  })

  const timeOffQ = useQuery({
    queryKey: ['hr', 'time-off', { person: personId }],
    queryFn: async () =>
      (await api.get<TimeOff[]>(`/hr/time-off/?person=${personId}`)).data,
    enabled: !!personId,
  })

  const assignmentsQ = useQuery({
    queryKey: ['consulting', 'assignments', { person: personId }],
    queryFn: async () =>
      (await api.get<ProjectAssignment[]>(`/consulting/assignments/?person=${personId}`)).data,
    enabled: !!personId,
  })
  // Supporting data for the "+ Add employment" drawer on the Employments tab.
  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<SubsidiaryLite[]>('/subsidiaries/')).data,
  })
  const positionsQ = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: async () => (await api.get<PositionLite[]>('/hr/positions/')).data,
  })
  const orgQ = useQuery({
    queryKey: ['organization'],
    queryFn: async () => (await api.get<OrgInfo>('/organization/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Person>) =>
      (await api.patch(`/hr/persons/${personId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'person', personId] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/persons/${personId}/`),
    onSuccess: () => {
      toast.success('Person deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
      navigate('/admin/hr/people')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Respect ?tab= query param so links from the Employees list can land
  // directly on the Employments tab. Unknown values fall back to 'profile'.
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as TabKey | null
  const initialTab: TabKey =
    tabParam && (['profile', 'employments', 'timeoff', 'projects', 'files'] as const).includes(tabParam)
      ? tabParam
      : 'profile'
  const [tab, setTab] = useState<TabKey>(initialTab)

  if (personQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (personQ.isError || !personQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Person not found.</p>
          <Link to="/admin/hr/people" className="text-blue-600 hover:underline">← Back to people</Link>
        </div>
      </AdminLayout>
    )
  }

  const person = personQ.data
  const employments = employmentsQ.data || []
  const contractorEngagements = contractorEngagementsQ.data || []
  const timeOff = timeOffQ.data || []
  const assignments = assignmentsQ.data || []
  const subsidiaries = subsidiariesQ.data || []
  const positions = (positionsQ.data || []).filter((p) => p.is_active && p.is_open)
  const org = orgQ.data

  const savePerson = (payload: Partial<Person>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Page header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/hr/people" className="text-muted-foreground hover:text-foreground hover:underline">← People</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{person.full_name}</h2>
            <div className="text-sm text-muted-foreground mt-0.5">
              {person.preferred_name && <span>"{person.preferred_name}" · </span>}
              {person.employment_count} employment{person.employment_count === 1 ? '' : 's'}
              {person.current_employment_id ? ' · currently employed' : ' · not currently employed'}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {person.current_employment_id ? (
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">Active</span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-gray-100 text-gray-700">No active employment</span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`person ${person.full_name}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete person</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['profile', 'employments', 'timeoff', 'projects', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'profile' && 'Profile'}
              {t === 'employments' && `Employments (${employments.length})`}
              {t === 'timeoff' && `Time Off (${timeOff.length})`}
              {t === 'projects' && `Projects (${assignments.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'profile' && <ProfileTab person={person} onSave={savePerson} />}
      {tab === 'employments' && (
        <EmploymentsTab
          personId={personId}
          employments={employments}
          contractorEngagements={contractorEngagements}
          loading={employmentsQ.isLoading || contractorEngagementsQ.isLoading}
          subsidiaries={subsidiaries}
          positions={positions}
          org={org}
        />
      )}
      {tab === 'timeoff' && <TimeOffTab rows={timeOff} loading={timeOffQ.isLoading} />}
      {tab === 'projects' && <ProjectsTab rows={assignments} loading={assignmentsQ.isLoading} />}
      {tab === 'files' && <FilesTab parent="hr.person" parentId={personId} />}
    </AdminLayout>
  )
}

// ─────────────────── Profile tab ───────────────────

function ProfileTab({
  person, onSave,
}: {
  person: Person
  onSave: (payload: Partial<Person>) => Promise<void>
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Identity">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="First name">
            <InlineText value={person.first_name} onSave={(v) => onSave({ first_name: v })} />
          </Row>
          <Row label="Last name">
            <InlineText value={person.last_name} onSave={(v) => onSave({ last_name: v })} />
          </Row>
          <Row label="Preferred name">
            <InlineText
              value={person.preferred_name}
              onSave={(v) => onSave({ preferred_name: v })}
              placeholder="Add a preferred name"
            />
          </Row>
          <Row label="Date of birth">
            <InlineDate value={person.date_of_birth} onSave={(v) => onSave({ date_of_birth: v })} />
          </Row>
          <Row label="National ID">
            <InlineText
              value={person.national_id}
              onSave={(v) => onSave({ national_id: v })}
              placeholder="Add national ID"
            />
          </Row>
          <Row label="Passport">
            <InlineText
              value={person.passport_number}
              onSave={(v) => onSave({ passport_number: v })}
              placeholder="Add passport number"
            />
          </Row>
          <Row label="Tax ID">
            <InlineText
              value={person.tax_id}
              onSave={(v) => onSave({ tax_id: v })}
              placeholder="Add personal tax ID"
            />
          </Row>
        </dl>
      </Card>

      <Card title="Contact">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Personal email">
            <InlineText
              value={person.personal_email}
              onSave={(v) => onSave({ personal_email: v })}
              placeholder="Add personal email"
            />
          </Row>
          <Row label="Mobile">
            <InlineText
              value={person.mobile}
              onSave={(v) => onSave({ mobile: v })}
              placeholder="Add mobile number"
            />
          </Row>
          <Row label="Photo URL">
            <InlineText
              value={person.photo_url}
              onSave={(v) => onSave({ photo_url: v })}
              placeholder="Add photo URL"
            />
          </Row>
        </dl>
      </Card>

      <Card title="Home address">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Address line">
            <InlineText
              value={person.address_line1}
              onSave={(v) => onSave({ address_line1: v })}
              placeholder="Street + number"
            />
          </Row>
          <Row label="City">
            <InlineText
              value={person.city}
              onSave={(v) => onSave({ city: v })}
              placeholder="Add city"
            />
          </Row>
          <Row label="Postal code">
            <InlineText
              value={person.postal_code}
              onSave={(v) => onSave({ postal_code: v })}
              placeholder="Add postal code"
            />
          </Row>
          <Row label="Country">
            <InlineText
              value={person.country_code}
              onSave={(v) => onSave({ country_code: (v || '').toUpperCase() })}
              placeholder="ISO 2-letter (e.g. RO)"
            />
          </Row>
        </dl>
      </Card>

      <Card title="Emergency contact">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Name">
            <InlineText
              value={person.emergency_contact_name}
              onSave={(v) => onSave({ emergency_contact_name: v })}
              placeholder="Add emergency contact name"
            />
          </Row>
          <Row label="Phone">
            <InlineText
              value={person.emergency_contact_phone}
              onSave={(v) => onSave({ emergency_contact_phone: v })}
              placeholder="Add emergency contact phone"
            />
          </Row>
        </dl>
      </Card>

      <Card title="Notes" className="lg:col-span-2">
        <InlineText
          value={person.notes}
          multiline
          onSave={(v) => onSave({ notes: v })}
          placeholder="HR-only notes about the person (not the contract)"
        />
      </Card>
    </div>
  )
}

// ─────────────────── Employments tab ───────────────────

const CONTRACTOR_STATUS_COLOR: Record<ContractorEngagement['status'], string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  PAUSED: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-slate-100 text-slate-700',
}

function EmploymentsTab({
  personId, employments, contractorEngagements, loading,
  subsidiaries, positions, org,
}: {
  personId: number
  employments: Employment[]
  contractorEngagements: ContractorEngagement[]
  loading: boolean
  subsidiaries: SubsidiaryLite[]
  positions: PositionLite[]
  org: OrgInfo | undefined
}) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<NewEmploymentInput>(emptyEmployment())

  useEffect(() => { if (open) setForm(emptyEmployment()) }, [open])

  const isHolding = org?.organization_type === 'HOLDING_SUBSIDIARIES'

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {
        person: personId,
        start_date: form.start_date,
        employment_type: form.employment_type,
        subsidiary: form.subsidiary,
        position: form.position,
        status: 'ACTIVE',
      }
      return (await api.post<Employment>('/hr/employments/', payload)).data
    },
    onSuccess: (created) => {
      toast.success('Employment created')
      qc.invalidateQueries({ queryKey: ['hr', 'employments'] })
      qc.invalidateQueries({ queryKey: ['hr', 'person', personId] })
      setOpen(false)
      navigate(`/admin/hr/employments/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const set = <K extends keyof NewEmploymentInput>(k: K, v: NewEmploymentInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Each Employment ties this person to a hiring entity, role, and dates. A person can have multiple
          Employments over time (rehires, transfers, contractor → employee conversions).
        </p>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-3 w-3 mr-1" /> Add employment
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : employments.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No employments yet for this person. Click <span className="font-semibold">Add employment</span> to hire them.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>#</Th>
                <Th>Job title</Th>
                <Th>Type</Th>
                <Th>Hiring entity</Th>
                <Th>Country</Th>
                <Th>Department</Th>
                <Th>Manager</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {employments.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/employments/${e.id}`)}
                >
                  <Td className="font-mono text-xs">{e.employee_number || <Dash />}</Td>
                  <Td className="text-sm">{e.job_title || <Dash />}</Td>
                  <Td className="text-xs">{EMPLOYMENT_TYPE_LABEL[e.employment_type]}</Td>
                  <Td className="text-sm">
                    {e.subsidiary_name ? (
                      <span>{e.subsidiary_code} · {e.subsidiary_name}</span>
                    ) : (
                      <span className="text-muted-foreground">Group entity</span>
                    )}
                  </Td>
                  <Td className="text-xs font-mono uppercase">{e.governing_country || <Dash />}</Td>
                  <Td className="text-sm">{e.department_name || <Dash />}</Td>
                  <Td className="text-sm">{e.manager_name || <Dash />}</Td>
                  <Td className="text-xs">{e.start_date || <Dash />}</Td>
                  <Td className="text-xs">{e.end_date || <Dash />}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${EMPLOYMENT_STATUS_COLOR[e.status]}`}>
                      {e.status.replace('_', ' ')}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contractor Engagements section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Contractor Engagements ({contractorEngagements.length})
          </h3>
        </div>
        {contractorEngagements.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-6 text-center text-muted-foreground text-sm">
            No contractor engagements for this person.
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/40">
                <tr>
                  <Th>#</Th>
                  <Th>Job title</Th>
                  <Th>Type</Th>
                  <Th>Partner</Th>
                  <Th>Contract</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {contractorEngagements.map((eng) => (
                  <tr
                    key={eng.id}
                    className="hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/admin/hr/contractor-engagements/${eng.id}`)}
                  >
                    <Td className="font-mono text-xs">{eng.engagement_number}</Td>
                    <Td className="text-sm">{eng.job_title || <Dash />}</Td>
                    <Td className="text-xs">{eng.contractor_type === 'DIRECT' ? 'Direct' : 'Agency'}</Td>
                    <Td className="text-sm">{eng.employer_partner_name || <Dash />}</Td>
                    <Td className="text-xs">{eng.active_contract_number || <Dash />}</Td>
                    <Td className="text-xs">{eng.start_date}</Td>
                    <Td className="text-xs">{eng.end_date || <Dash />}</Td>
                    <Td>
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${CONTRACTOR_STATUS_COLOR[eng.status]}`}>
                        {eng.status}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecordDrawer
        open={open}
        onOpenChange={setOpen}
        title="Add employment"
        description="Capture the essentials to hire this person. Contract type, probation, compensation, leave balance, and documents are filled in on the Employment record page after creation."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Hire"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ne-start">Start date *</FieldLabel>
            <DatePicker id="ne-start" required captionLayout="dropdown"
              value={form.start_date} onChange={(v) => set('start_date', v)} />
          </Field>

          <Field>
            <FieldLabel htmlFor="ne-entity">Hiring entity {isHolding && '*'}</FieldLabel>
            {isHolding ? (
              <SearchableSelect
                id="ne-entity"
                value={form.subsidiary ? String(form.subsidiary) : NONE}
                onChange={(v) => set('subsidiary', v === NONE ? null : Number(v))}
                placeholder="— select subsidiary —"
                options={[
                  { value: NONE, label: '— select subsidiary —' },
                  ...subsidiaries.map((s) => ({
                    value: String(s.id), label: s.name, hint: s.code,
                  })),
                ]}
              />
            ) : (
              <div id="ne-entity" className="flex h-10 items-center px-3 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground">
                {org?.legal_name || org?.group_name || '—'}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="ne-type">Employment type</FieldLabel>
            <Select value={form.employment_type}
              onValueChange={(v) => set('employment_type', v as NewEmploymentInput['employment_type'])}>
              <SelectTrigger id="ne-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(EMPLOYMENT_TYPE_LABEL) as NewEmploymentInput['employment_type'][]).map((k) => (
                  <SelectItem key={k} value={k}>{EMPLOYMENT_TYPE_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="ne-pos">Position</FieldLabel>
            <SearchableSelect
              id="ne-pos"
              value={form.position ? String(form.position) : NONE}
              onChange={(v) => set('position', v === NONE ? null : Number(v))}
              placeholder="— select a seat —"
              options={[
                { value: NONE, label: '— select a seat —' },
                ...positions.map((p) => ({ value: String(p.id), label: p.title, hint: p.code })),
              ]}
            />
            <FieldDescription>
              Org-chart seat this employment fills. Department and manager are derived automatically from this seat.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function emptyEmployment(): NewEmploymentInput {
  return {
    start_date: new Date().toISOString().slice(0, 10),
    employment_type: 'FULL_TIME' as const,
    subsidiary: null,
    position: null,
  }
}

// ─────────────────── Time Off tab ───────────────────

function TimeOffTab({ rows, loading }: { rows: TimeOff[]; loading: boolean }) {
  if (loading) return <p className="text-muted-foreground">Loading…</p>
  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
        No time-off records for this person.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Type</Th>
            <Th>Start</Th>
            <Th>End</Th>
            <Th>Days</Th>
            <Th>Reason</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-muted/40">
              <Td className="text-xs">{LEAVE_LABEL[r.leave_type]}</Td>
              <Td className="text-xs">{r.start_date}</Td>
              <Td className="text-xs">{r.end_date}</Td>
              <Td className="text-sm">{r.days}</Td>
              <Td className="text-sm">{r.reason || <Dash />}</Td>
              <Td>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${LEAVE_STATUS_COLOR[r.status]}`}>
                  {r.status}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────── Projects tab ───────────────────

function ProjectsTab({ rows, loading }: { rows: ProjectAssignment[]; loading: boolean }) {
  if (loading) return <p className="text-muted-foreground">Loading…</p>
  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
        No project assignments for this person.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Project</Th>
            <Th>Role</Th>
            <Th>Start</Th>
            <Th>End</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40">
              <Td className="text-sm">
                <Link to={`/admin/consulting/projects/${a.project}`} className="text-blue-600 hover:underline">
                  {a.project_code} — {a.project_name}
                </Link>
              </Td>
              <Td className="text-xs">{a.role}</Td>
              <Td className="text-xs">{a.start_date}</Td>
              <Td className="text-xs">{a.end_date || <Dash />}</Td>
              <Td>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${ASSIGNMENT_STATUS_COLOR[a.status]}`}>
                  {a.status}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}
